import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import api from "../../../config/URL";
import "../../../styles/admin.css";
import { FiAlertTriangle } from "react-icons/fi";

function FreeCashAdd() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    event_key: Yup.string().required("*Event key is required"),
    event_name: Yup.string().required("*Event name is required"),
    reward_amount: Yup.number()
      .required("*Reward amount is required")
      .positive("*Reward amount must be a positive number")
      .max(
        99999999.99,
        "*Reward amount must be less than or equal to 99999999.99"
      ),
  });

  const formik = useFormik({
    initialValues: {
      event_key: "",
      event_name: "",
      reward_amount: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const payload = {
        event_key: values.event_key,
        event_name: values.event_name,
        reward_amount: parseFloat(values.reward_amount),
      };

      setLoadIndicator(true);
      try {
        const response = await api.post(`admin/freecoins`, payload);
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          navigate("/freeCash");
        } else if (response.status === 422) {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error.response) {
          const { status, data } = error.response;

          if (status === 422 && data.errors) {
            Object.keys(data.errors).forEach((key) => {
              data.errors[key].forEach((msg) => {
                toast.error(msg, {
                  icon: <FiAlertTriangle className="text-warning" />,
                });
              });
            });
          } else if (data.message) {
            toast.error(data.message, {
              icon: <FiAlertTriangle className="text-danger" />,
            });
          } else {
            toast.error("An unexpected error occurred.", {
              icon: <FiAlertTriangle className="text-danger" />,
            });
          }
        } else {
          toast.error("Network or server error.", {
            icon: <FiAlertTriangle className="text-danger" />,
          });
        }
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        {loading ? (
          <div className="loader-container">
            <div className="loader">
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32"></circle>
              </svg>
            </div>
          </div>
        ) : (
          <>
            <div className="card shadow border-0 mb-3">
              <div className="row p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h1 className="h4 ls-tight">Add Free Cash</h1>
                  <Link to="/freeCash">
                    <button type="button" className="btn btn-light btn-sm">
                      <span>Back</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <div
              className="container card shadow border-0"
              style={{ minHeight: "60vh" }}
            >
              <div className="row mt-3 mb-5 p-3">
                {/* Event Key */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Event Key <span className="text-danger">*</span>
                  </label>
                  <select
                    name="event_key"
                    className={`form-select ${
                      formik.touched.event_key && formik.errors.event_key
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.event_key}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">Select the key</option>
                    <option value="new_user">New User</option>
                  </select>
                  {formik.touched.event_key && formik.errors.event_key && (
                    <div className="invalid-feedback">
                      {formik.errors.event_key}
                    </div>
                  )}
                </div>

                {/* Event Name */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Event Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="event_name"
                    className={`form-control ${
                      formik.touched.event_name && formik.errors.event_name
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.event_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter event name"
                  />
                  {formik.touched.event_name && formik.errors.event_name && (
                    <div className="invalid-feedback">
                      {formik.errors.event_name}
                    </div>
                  )}
                </div>

                {/* Reward Amount */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Reward Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    name="reward_amount"
                    className={`form-control ${
                      formik.touched.reward_amount &&
                      formik.errors.reward_amount
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.reward_amount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter reward amount"
                    step="0.01"
                    min="0"
                  />
                  {formik.touched.reward_amount &&
                    formik.errors.reward_amount && (
                      <div className="invalid-feedback">
                        {formik.errors.reward_amount}
                      </div>
                    )}
                </div>
              </div>

              {/* Submit */}
              <div className="hstack p-2 mt-5">
                <button
                  type="submit"
                  className="btn btn-sm btn-button"
                  disabled={loadIndicator}
                >
                  {loadIndicator && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  Add Free Cash
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

export default FreeCashAdd;
